import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { Badge } from '../../components/ui/badge';
import { Pagination } from '../../components/ui/pagination';
import { useToast } from '../../components/ui/toast';
import { Edit, Trash2, Search, Plus, ArrowUpDown, Copy } from 'lucide-react';
import { useWorkspace } from '../../context/workspace-context';
import {
  useCourses,
  useDeleteCourse,
  useDuplicateCourse,
} from '../../hooks/use-course';
import { getErrorMessage } from '../../helpers/get-error-message';

const PAGE_SIZE = 20;

export default function CourseList() {
  const navigate = useNavigate();
  const { currentWorkspace, isLoadingWorkspaces } = useWorkspace();
  const [sortField, setSortField] = useState<
    'name' | 'status' | 'created_at' | null
  >(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);

  const { data, isLoading, error } = useCourses(
    currentWorkspace?.id,
    currentPage,
    PAGE_SIZE,
    filterStatus,
    searchTerm,
  );

  const deleteCourse = useDeleteCourse();
  const duplicateCourse = useDuplicateCourse();
  const { showToast } = useToast();
  const [pendingDuplicate, setPendingDuplicate] = useState<{
    id: number;
    name: string;
  } | null>(null);

  const totalPages = data?.totalPages || 0;

  const handleSearch = () => {
    setSearchTerm(searchInput);
    setCurrentPage(1);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const sortCourses = (field: 'name' | 'status' | 'created_at') => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const sortedCourses = useMemo(() => {
    const courses = data?.courses ?? [];
    if (!sortField) return courses;

    return [...courses].sort((a, b) => {
      const aValue = a[sortField];
      const bValue = b[sortField];

      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  }, [data?.courses, sortField, sortDirection]);

  const handleDuplicateConfirm = async () => {
    if (!pendingDuplicate || !currentWorkspace?.id) return;

    try {
      const { id: newId } = await duplicateCourse.mutateAsync({
        courseId: pendingDuplicate.id,
        organisationId: currentWorkspace.id,
      });
      setPendingDuplicate(null);
      showToast('Course duplicated', 'success');
      navigate(`/edit/${newId}`);
    } catch (error) {
      setPendingDuplicate(null);
      showToast(
        `Failed to duplicate course: ${getErrorMessage(error, 'Please try again.')}`,
        'error',
      );
    }
  };

  const handleDeleteCourse = async (id: number) => {
    if (
      !confirm(
        'Are you sure you want to delete this course? This action cannot be undone.',
      )
    ) {
      return;
    }

    if (!currentWorkspace?.id) {
      alert('No workspace selected');
      return;
    }

    try {
      await deleteCourse.mutateAsync({
        courseId: id,
        organisationId: currentWorkspace.id,
      });
    } catch (error) {
      alert(
        `Failed to delete course: ${getErrorMessage(error, 'Please try again.')}`,
      );
    }
  };

  const statusColors: { [key: string]: string } = {
    Published: 'bg-green-500',
    Draft: 'bg-yellow-500',
  };

  const loading = isLoading || isLoadingWorkspaces;

  return (
    <Card>
      <CardContent className="pt-6">
        <h1 className="text-2xl font-bold mb-4">Courses</h1>
        {error && (
          <div className="text-red-500 mb-4 p-4 bg-red-50 rounded">
            Error:{' '}
            {error instanceof Error ? error.message : 'An error occurred'}
          </div>
        )}
        <div className="flex justify-between items-center mb-4">
          <div className="flex items-center space-x-2">
            <Input
              placeholder="Search courses..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={handleKeyDown}
              className="w-64"
              disabled={loading}
            />
            <Button
              variant="outline"
              size="icon"
              onClick={handleSearch}
              disabled={loading}
            >
              <Search className="h-4 w-4" />
            </Button>
          </div>
          <div className="flex items-center space-x-2">
            <Select
              value={filterStatus}
              onValueChange={(value) => {
                setFilterStatus(value);
                setCurrentPage(1);
              }}
              disabled={loading}
            >
              <SelectTrigger className="w-[180px]">
                <SelectValue>
                  {filterStatus === 'all' ? 'All Statuses' : filterStatus}
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="Published">Published</SelectItem>
                <SelectItem value="Draft">Draft</SelectItem>
              </SelectContent>
            </Select>
            <Button
              onClick={() => navigate('/courses/new')}
              className="bg-accent-orange hover:bg-accent-orange/90 text-white"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Course
            </Button>
          </div>
        </div>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[300px]">
                <Button
                  variant="ghost"
                  size="table-header"
                  onClick={() => sortCourses('name')}
                >
                  Course Name
                  <ArrowUpDown className="ml-2 h-4 w-4" />
                </Button>
              </TableHead>
              <TableHead>
                <Button
                  variant="ghost"
                  size="table-header"
                  onClick={() => sortCourses('status')}
                >
                  Status
                  <ArrowUpDown className="ml-2 h-4 w-4" />
                </Button>
              </TableHead>
              <TableHead>Team / Department</TableHead>
              <TableHead>
                <Button
                  variant="ghost"
                  size="table-header"
                  onClick={() => sortCourses('created_at')}
                >
                  Created At
                  <ArrowUpDown className="ml-2 h-4 w-4" />
                </Button>
              </TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={5} className="text-center py-8">
                  Loading courses...
                </TableCell>
              </TableRow>
            ) : sortedCourses.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={5}
                  className="text-center py-8 text-gray-500"
                >
                  No courses found
                </TableCell>
              </TableRow>
            ) : (
              sortedCourses.map((course) => (
                <TableRow key={course.id}>
                  <TableCell className="font-medium">{course.name}</TableCell>
                  <TableCell>
                    <Badge
                      className={`${statusColors[course.status]} text-white`}
                    >
                      {course.status}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    {course.course_departments?.map((cd) => (
                      <Badge
                        key={cd.departments.id}
                        variant="outline"
                        className="mr-1"
                      >
                        {cd.departments.name}
                      </Badge>
                    ))}
                  </TableCell>
                  <TableCell>
                    {new Date(course.created_at).toLocaleDateString()}
                  </TableCell>
                  <TableCell>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => navigate(`/edit/${course.id}`)}
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      title="Duplicate course"
                      onClick={() =>
                        setPendingDuplicate({
                          id: course.id,
                          name: course.name,
                        })
                      }
                    >
                      <Copy className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDeleteCourse(course.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
        />
        <Dialog
          open={!!pendingDuplicate}
          onOpenChange={(open) => {
            if (!open && !duplicateCourse.isPending) {
              setPendingDuplicate(null);
            }
          }}
        >
          <DialogContent className="sm:max-w-[420px]">
            <DialogHeader>
              <DialogTitle>Duplicate this course?</DialogTitle>
              <DialogDescription>
                A new draft copy of <strong>{pendingDuplicate?.name}</strong>{' '}
                will be created. The copy will include all content, levels, and
                questions, but no user progress.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setPendingDuplicate(null)}
                disabled={duplicateCourse.isPending}
              >
                Cancel
              </Button>
              <Button
                onClick={handleDuplicateConfirm}
                disabled={duplicateCourse.isPending}
                className="bg-accent-orange hover:bg-accent-orange/90 text-white"
              >
                {duplicateCourse.isPending ? 'Duplicating...' : 'Duplicate'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
}
